import DOMPurify from 'dompurify';

/**
 * Safe CSS properties allowed in template HTML style attributes.
 * Excludes properties that enable CSS-based attacks:
 * - position/z-index (UI redressing)
 * - display:none (content hiding)
 * - background-image/url() (data exfiltration)
 */
const SAFE_CSS_PROPERTIES = new Set([
  'color',
  'background-color',
  'font-size',
  'font-weight',
  'font-style',
  'font-family',
  'text-align',
  'text-decoration',
  'text-transform',
  'line-height',
  'letter-spacing',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-collapse',
  'border-spacing',
  'border-color',
  'border-width',
  'border-style',
  'width',
  'max-width',
  'min-width',
  'height',
  'max-height',
  'min-height',
  'white-space',
  'word-break',
  'overflow-wrap',
  'vertical-align',
  'list-style-type',
  'list-style-position',
]);

const TEMPLATE_CONTENT_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'table',
  'tr',
  'td',
  'th',
  'thead',
  'tbody',
  'span',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'blockquote',
  'pre',
  'code',
  'hr',
];

const TEMPLATE_SUBJECT_TAGS = ['strong', 'b', 'em', 'i', 'u', 'span'];

/**
 * Filters a CSS style string to only allow safe properties.
 * Blocks url(), expression(), and any property not in the safelist.
 */
function sanitizeCssStyle(styleValue: string): string {
  const tempEl = document.createElement('span');
  tempEl.style.cssText = styleValue;

  const safeStyles: string[] = [];
  for (const prop of tempEl.style) {
    if (SAFE_CSS_PROPERTIES.has(prop)) {
      const value = tempEl.style.getPropertyValue(prop);
      // Block any value containing url() or expression()
      if (!value.includes('url(') && !value.includes('expression(')) {
        safeStyles.push(`${prop}: ${value}`);
      }
    }
  }
  return safeStyles.join('; ');
}

/**
 * Sanitize template body/content HTML.
 * Allows formatting tags, tables, links, and safe inline styles.
 */
export function sanitizeTemplateContent(html: string): string {
  // Add hook to filter CSS properties within style attributes
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'style') {
      data.attrValue = sanitizeCssStyle(data.attrValue);
    }
  });

  const result = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: TEMPLATE_CONTENT_TAGS,
    ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });

  DOMPurify.removeHook('uponSanitizeAttribute');
  return result;
}

/**
 * Sanitize template subject line HTML.
 * Only allows basic inline formatting, no styles or links.
 */
export function sanitizeTemplateSubject(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: TEMPLATE_SUBJECT_TAGS,
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  });
}
